"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePurchaseAuth } from "@/contexts/purchase-auth-provider";
import { useShopCatalog } from "@/contexts/shop-catalog-provider";
import { useRemoveUnqualifiedGiftLines } from "@/hooks/shop/use-remove-unqualified-gift-lines";
import type { ShopCart } from "@/hooks/shop/use-shop-cart";
import { recordConversion } from "@/lib/conversion-events";
import { trackShopAddShippingInfo, trackShopBeginCheckout, trackShopPurchase } from "@/lib/gtm-shop-events";
import { type PostSignupBenefitTarget, packageIncludedItemsToBenefitTargets } from "@/lib/post-signup-service-benefit";
import { SHOP_CHECKOUT_QUERY_PARAM, type ShopSection } from "@/lib/shop-links";
import { useToast } from "@/lib/use-toast";
import { ItemType } from "@/types/enums/item-type";
import { MembershipShopBillingStart } from "@/types/enums/membership";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import type { PurchaseBuyer, PurchaseCharges } from "@/types/shop";
import { cartCanShip, cartRequiresPickupLocation, cartSoldAtLocationIds, shippingSubtotal } from "./cart-line-utils";
import FulfillmentMethodStep from "./fulfillment-method-step";
import { isMembershipBillingStartFuture } from "./memberships/utils";
import PickupLocationStep from "./pickup-location-step";
import { giftOfferForLine } from "./promotion-messaging";
import ShippingAddressStep from "./shipping-address-step";
import BrowseStage from "./stages/browse-stage";
import CheckoutStage from "./stages/checkout-stage";
import CompleteStage from "./stages/complete-stage";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { refetchShopCatalogQuery } from "@/query/shop/shop-query";
import { tenantQueryOptions } from "@/query/tenant";
interface PurchaseFlowProps {
  tenantSlug: string;
  initialSection?: ShopSection | null;
  cart: ShopCart;
  /** Opened from a product page's checkout button: go straight to checkout. */
  startAtCheckout?: boolean;
  onDeliveryChoice?: (method: ShopFulfillmentMethod | null) => void;
}
type ActiveStage = "browse" | "fulfillment" | "pickup" | "shipping" | "checkout";
type Stage = ActiveStage | "complete";
type CatalogRefreshStatus = "idle" | "refreshing" | "ready" | "error";
export interface ScheduledMembershipConfirmation {
  name: string;
  activationTiming: string;
}
interface CompletedPurchaseSummary {
  purchasedServiceIds: string[];
  packageBenefitTargets: PostSignupBenefitTarget[];
  scheduledMembership: ScheduledMembershipConfirmation | null;
}
interface ActivePurchaseFlowProps extends PurchaseFlowProps {
  stage: ActiveStage;
  onStageChange: (stage: ActiveStage) => void;
  pickupLocationId: string | null;
  onPickupLocationChange: (locationId: string | null) => void;
  fulfillmentMethod: ShopFulfillmentMethod | null;
  onFulfillmentMethodChange: (method: ShopFulfillmentMethod | null) => void;
  onPaymentComplete: (summary: CompletedPurchaseSummary) => void;
}

/**
 * Active browse-to-checkout state. The surrounding shop-shell catalog
 * provider stays mounted for every stage so shared merchandising and pricing
 * remain synchronized across the announcement bar, browse, and checkout.
 */
function ActivePurchaseFlow({
  tenantSlug,
  initialSection,
  stage,
  onStageChange,
  pickupLocationId,
  onPickupLocationChange: setPickupLocationId,
  fulfillmentMethod: chosenFulfillmentMethod,
  onFulfillmentMethodChange: setFulfillmentMethod,
  onPaymentComplete,
  cart,
  startAtCheckout = false
}: ActivePurchaseFlowProps) {
  const {
    buyer
  } = usePurchaseAuth();
  const {
    catalog,
    isLoading: isCatalogLoading
  } = useShopCatalog();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    data: publicTenant
  } = useQuery(tenantQueryOptions.public(tenantSlug));
  const preferredPickupLocationId = buyer?.primaryLocationId ?? buyer?.locationId ?? null;
  const [shippingBuyer, setShippingBuyer] = useState<PurchaseBuyer | null>(null);
  useRemoveUnqualifiedGiftLines(cart);
  const canShip = useMemo(() => cartCanShip(cart.lines, catalog.items, catalog.shipping), [cart.lines, catalog.items, catalog.shipping]);
  // Matches the server: a shippable cart ships unless the patient picks pickup.
  const fulfillmentMethod = canShip ? chosenFulfillmentMethod ?? ShopFulfillmentMethod.SHIP : null;
  const nonShippableItemRequiresPickupLocation = useMemo(() => cartRequiresPickupLocation(cart.lines, catalog.items), [cart.lines, catalog.items]);
  const requiresPickupLocationFor = (method: ShopFulfillmentMethod | null) => nonShippableItemRequiresPickupLocation || method === ShopFulfillmentMethod.PICKUP;
  const {
    data: frontPageData,
    isError: isFrontPageError,
    isFetching: isFrontPageFetching
  } = useQuery({
    ...selfScheduleQueryOptions.frontPage(),
    enabled: nonShippableItemRequiresPickupLocation || canShip
  });
  const eligibleLocationsFor = (method: ShopFulfillmentMethod | null) => {
    const allowedIds = cartSoldAtLocationIds(cart.lines, catalog.items, method);
    return {
      allowedIds,
      locations: frontPageData?.locations?.filter(location => !location.isVirtual && !location.isArchived && (!allowedIds || allowedIds.includes(location.id))) ?? null
    };
  };
  const {
    allowedIds: cartLocationIds,
    locations: pickupEligibleLocations
  } = eligibleLocationsFor(fulfillmentMethod);
  const needsExplicitPickupLocationFor = (method: ShopFulfillmentMethod | null) => requiresPickupLocationFor(method) && eligibleLocationsFor(method).locations?.length !== 1;
  const needsExplicitPickupLocation = needsExplicitPickupLocationFor(fulfillmentMethod);
  // The server fills in a clinic's only location, but not the only location a
  // cart is limited to, so that one is sent explicitly.
  const cartOnlyLocationId = cartLocationIds && pickupEligibleLocations?.length === 1 ? pickupEligibleLocations[0].id : null;
  const needsShippingAddress = fulfillmentMethod === ShopFulfillmentMethod.SHIP;
  const handlePaymentComplete = (totalCents: number, charges: PurchaseCharges) => {
    const purchasedServiceIds = cart.lines.flatMap(line => line.itemType === ItemType.SERVICE ? Array.from({
      length: line.quantity
    }, () => line.itemId) : []);
    const packageBenefitTargets = cart.lines.flatMap(line => line.itemType === ItemType.PACKAGE ? packageIncludedItemsToBenefitTargets(line.snapshot.packageIncludedItems) : []);
    const membershipLine = cart.lines.find(line => line.kind === "membership");
    const billingStart = membershipLine?.snapshot.membership?.shopBillingStart;
    const hasScheduledMembership = isMembershipBillingStartFuture(billingStart, publicTenant?.defaultTimezone);
    const scheduledMembership = membershipLine && hasScheduledMembership ? {
      name: membershipLine.snapshot.name,
      activationTiming: billingStart === MembershipShopBillingStart.FIRST_OF_MONTH ? "the next 1st" : "the next 15th"
    } : null;
    trackShopPurchase({
      transactionId: cart.checkoutNonce ?? uuidv4(),
      lines: cart.lines,
      totalCents,
      charges
    });
    recordConversion("purchase_completed", {
      value: totalCents / 100,
      currency: "USD"
    });
    cart.clear();
    onPaymentComplete({
      purchasedServiceIds,
      packageBenefitTargets,
      scheduledMembership
    });
  };
  const pickupLocationsReady = (method: ShopFulfillmentMethod | null) => {
    if (!requiresPickupLocationFor(method)) return true;
    if (isFrontPageError || frontPageData === null) {
      toast({
        title: "Locations unavailable",
        description: "We couldn't load available locations for this purchase. Please try again.",
        variant: "error"
      });
      return false;
    }
    if (!frontPageData || isFrontPageFetching) {
      toast({
        title: "Still loading locations",
        description: "Please wait a moment, then continue to checkout.",
        variant: "error"
      });
      return false;
    }
    return true;
  };
  const stageAfterFulfillmentChoice = (method: ShopFulfillmentMethod | null): ActiveStage => needsExplicitPickupLocationFor(method) ? "pickup" : method === ShopFulfillmentMethod.SHIP ? "shipping" : "checkout";
  const handleCheckout = () => {
    if (canShip) {
      trackShopBeginCheckout(cart.lines);
      onStageChange("fulfillment");
      return;
    }
    if (!pickupLocationsReady(null)) return;
    trackShopBeginCheckout(cart.lines);
    onStageChange(stageAfterFulfillmentChoice(null));
  };
  const handleSelectFulfillmentMethod = (method: ShopFulfillmentMethod) => {
    if (!pickupLocationsReady(method)) return;
    setFulfillmentMethod(method);
    onStageChange(stageAfterFulfillmentChoice(method));
  };
  const handleShippingUnavailable = (message: string) => {
    toast({
      title: "Shipping unavailable",
      description: message,
      variant: "error"
    });
    setFulfillmentMethod(null);
    onStageChange("fulfillment");
    // A failed refresh leaves the old catalog; the choice step still works.
    refetchShopCatalogQuery(queryClient, tenantSlug, buyer?.id).catch(() => undefined);
  };
  const handleBackToFulfillmentOrBrowse = () => onStageChange(canShip ? "fulfillment" : "browse");
  const autoCheckoutDone = useRef(false);
  const readyForAutoCheckout = startAtCheckout && stage === "browse" && !isCatalogLoading &&
  // Fulfillment checks read each line's catalog item; a failed or stale catalog
  // leaves the patient on the cart instead of skipping those steps.
  cart.lines.every(line => !("itemId" in line) || catalog.items.some(item => item.id === line.itemId) || giftOfferForLine(catalog.promotions, line) !== null) && cart.lines.length > 0 && (
  // A failed locations load still runs checkout, which explains the failure.
  !nonShippableItemRequiresPickupLocation || !isFrontPageFetching && (frontPageData !== undefined || isFrontPageError));
  useEffect(() => {
    if (!readyForAutoCheckout || autoCheckoutDone.current) return;
    autoCheckoutDone.current = true;
    const url = new URL(window.location.href);
    url.searchParams.delete(SHOP_CHECKOUT_QUERY_PARAM);
    window.history.replaceState(window.history.state, "", url.href);
    handleCheckout();
    // Runs once, when the cart, catalog, and locations are ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyForAutoCheckout]);
  const handleSelectPickupLocation = (locationId: string) => {
    setPickupLocationId(locationId);
    trackShopAddShippingInfo(cart.lines, "Pickup");
    onStageChange(needsShippingAddress ? "shipping" : "checkout");
  };
  const handleBackFromShipping = () => {
    if (needsExplicitPickupLocation) {
      setPickupLocationId(null);
      onStageChange("pickup");
    } else {
      handleBackToFulfillmentOrBrowse();
    }
  };
  const handleBackFromCheckout = () => {
    if (needsShippingAddress) {
      onStageChange("shipping");
    } else if (needsExplicitPickupLocation) {
      setPickupLocationId(null);
      onStageChange("pickup");
    } else {
      handleBackToFulfillmentOrBrowse();
    }
  };
  if (stage === "fulfillment" && catalog.shipping && canShip) {
    return <FulfillmentMethodStep shipping={catalog.shipping} shippableSubtotalCents={shippingSubtotal(cart.lines, catalog.items)} selectedMethod={chosenFulfillmentMethod} pickupAvailable={eligibleLocationsFor(ShopFulfillmentMethod.PICKUP).locations?.length !== 0} onSelectMethod={handleSelectFulfillmentMethod} onBack={() => onStageChange("browse")} />;
  }
  const handleChangePickupLocation = () => {
    setPickupLocationId(null);
    onStageChange("pickup");
  };
  if (stage === "pickup") {
    return <div className="motion-preset-slide-right-sm mx-auto">
        <PickupLocationStep selectedLocationId={pickupLocationId} preferredLocationId={preferredPickupLocationId} allowedLocationIds={cartLocationIds} onSelectLocation={handleSelectPickupLocation} onBack={handleBackToFulfillmentOrBrowse} />
      </div>;
  }
  const currentShippingBuyer = shippingBuyer?.id === buyer?.id ? shippingBuyer : null;
  if (stage === "shipping" || stage === "checkout" && needsShippingAddress && !currentShippingBuyer) {
    return <ShippingAddressStep savedBuyer={currentShippingBuyer} onBack={handleBackFromShipping} onContinue={buyer => {
      setShippingBuyer(buyer);
      trackShopAddShippingInfo(cart.lines, "Shipping");
      onStageChange("checkout");
    }} />;
  }
  if (stage === "checkout") {
    return <CheckoutStage lines={cart.lines} totalCents={cart.totalCents} checkoutNonce={cart.checkoutNonce} pickupLocationId={needsExplicitPickupLocation ? pickupLocationId : cartOnlyLocationId} shippingBuyer={needsShippingAddress ? currentShippingBuyer : null} fulfillmentMethod={fulfillmentMethod} onChangeFulfillmentMethod={canShip ? () => onStageChange("fulfillment") : undefined} onShippingUnavailable={handleShippingUnavailable} onRemoveLine={cart.removeLine} onReplaceLine={cart.replaceLine} onAddLine={cart.addLine} onPaymentComplete={handlePaymentComplete} onBack={handleBackFromCheckout} onChangeLocation={needsExplicitPickupLocation ? handleChangePickupLocation : undefined} onChangeShippingAddress={needsShippingAddress ? () => onStageChange("shipping") : undefined} />;
  }
  return <BrowseStage initialSection={initialSection} lines={cart.lines} onAddLine={cart.addLine} onReplaceLine={cart.replaceLine} onUpdateQuantity={cart.updateQuantity} onRemoveLine={cart.removeLine} onCheckout={handleCheckout} fulfillmentMethod={fulfillmentMethod} />;
}

/**
 * Top-level state machine for the online shop. The shop-shell catalog provider
 * owns one patient-aware query subscription across every purchase stage.
 */
const PurchaseFlow: React.FC<PurchaseFlowProps> = ({
  tenantSlug,
  initialSection,
  cart,
  startAtCheckout,
  onDeliveryChoice
}) => {
  const queryClient = useQueryClient();
  const {
    buyer
  } = usePurchaseAuth();
  const [stage, setStage] = useState<Stage>("browse");
  // Lives above ActivePurchaseFlow, which remounts when the buyer signs in at
  // checkout; the location they already picked must survive that.
  const [pickupLocationId, setPickupLocationId] = useState<string | null>(null);
  const [fulfillmentMethod, setFulfillmentMethod] = useState<ShopFulfillmentMethod | null>(null);
  const chooseFulfillmentMethod = (method: ShopFulfillmentMethod | null) => {
    setFulfillmentMethod(method);
    onDeliveryChoice?.(method);
  };
  const [purchasedServiceIds, setPurchasedServiceIds] = useState<string[]>([]);
  const [packageBenefitTargets, setPackageBenefitTargets] = useState<PostSignupBenefitTarget[]>([]);
  const [scheduledMembership, setScheduledMembership] = useState<ScheduledMembershipConfirmation | null>(null);
  const [catalogRefreshStatus, setCatalogRefreshStatus] = useState<CatalogRefreshStatus>("idle");
  const refreshCatalog = async (): Promise<boolean> => {
    setCatalogRefreshStatus("refreshing");
    try {
      await refetchShopCatalogQuery(queryClient, tenantSlug, buyer?.id);
      setCatalogRefreshStatus("ready");
      return true;
    } catch {
      setCatalogRefreshStatus("error");
      return false;
    }
  };
  if (stage === "complete") {
    return <CompleteStage tenantSlug={tenantSlug} purchasedServiceIds={purchasedServiceIds} packageBenefitTargets={packageBenefitTargets} scheduledMembership={scheduledMembership} isShopAgainLoading={catalogRefreshStatus === "refreshing"} shopAgainError={catalogRefreshStatus === "error"} onShopAgain={async () => {
      if (catalogRefreshStatus !== "ready" && !(await refreshCatalog())) {
        return;
      }
      setPurchasedServiceIds([]);
      setPackageBenefitTargets([]);
      setScheduledMembership(null);
      setCatalogRefreshStatus("idle");
      setPickupLocationId(null);
      chooseFulfillmentMethod(null);
      setStage("browse");
    }} />;
  }
  return <ActivePurchaseFlow key={buyer?.id ?? "signed-out"} tenantSlug={tenantSlug} initialSection={initialSection} cart={cart} startAtCheckout={startAtCheckout} stage={stage} onStageChange={setStage} pickupLocationId={pickupLocationId} onPickupLocationChange={setPickupLocationId} fulfillmentMethod={fulfillmentMethod} onFulfillmentMethodChange={chooseFulfillmentMethod} onPaymentComplete={summary => {
    setPurchasedServiceIds(summary.purchasedServiceIds);
    setPackageBenefitTargets(summary.packageBenefitTargets);
    setScheduledMembership(summary.scheduledMembership);
    setStage("complete");
    void refreshCatalog();
  }} />;
};
export default PurchaseFlow;